/**
 * FH hero contrast (2026-09-28): text over banner and slideshow photos always meets WCAG contrast.
 * For each slide with text over its photo, this measures the photo right behind every line of text and adds a
 * dark band from the top of the photo down to just below the text, only as dark as that photo needs:
 *   - large text (24px+, or 18.7px+ bold) needs 3:1, smaller text 4.5:1 (plus a small safety margin);
 *   - a dark photo gets no band at all, a light one gets just enough;
 *   - the band's height follows the text, so more or less text, other screen sizes and new photos all adjust.
 * Re-checked when photos or fonts load and when the window is resized. If a photo can't be read, the band
 * uses a fixed strength that passes even on a white photo. Styles and the no-JavaScript fallback are in
 * fh-hero-contrast.css. Rollback: remove the two hero contrast lines at the end of snippets/freshdesk.liquid.
 */
(function () {
  'use strict';
  if (window.__fhHeroContrast) return;
  window.__fhHeroContrast = true;

  var SAFE = 0.55;   /* passes 4.5:1 for white text on a pure white photo */
  var FADE = 100;    /* px the band takes to fade out below the text */
  var PAD = 24;      /* px of full-strength band below the last line */

  function lin(c) { c /= 255; return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); }
  function lum(r, g, b) { return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b); }
  function rgba(str) {
    var m = String(str).match(/rgba?\(([^)]+)\)/);
    if (!m) return null;
    var p = m[1].split(/[ ,/]+/).filter(Boolean).map(parseFloat);
    return { r: p[0], g: p[1], b: p[2], a: p.length > 3 ? p[3] : 1 };
  }

  /* Elements that hold visible text directly (headline, subheading, lines of the description). */
  function textItems(box) {
    var out = [];
    box.querySelectorAll('*').forEach(function (el) {
      if (el.closest('.banner__buttons')) return; /* the button has its own solid background */
      var own = Array.prototype.some.call(el.childNodes, function (n) { return n.nodeType === 3 && n.textContent.trim(); });
      if (!own) return;
      var r = el.getBoundingClientRect();
      if (!r.width || !r.height) return;
      out.push(el);
    });
    return out;
  }
  function target(el) {
    var cs = getComputedStyle(el);
    var size = parseFloat(cs.fontSize) || 16;
    var bold = (parseInt(cs.fontWeight, 10) || 400) >= 700;
    return (size >= 24 || (size >= 18.66 && bold)) ? 3.2 : 4.7; /* WCAG 3 / 4.5, with a margin */
  }

  function band(media) {
    var b = media.querySelector(':scope > .fh-hero-band');
    if (!b) {
      b = document.createElement('div');
      b.className = 'fh-hero-band';
      b.setAttribute('aria-hidden', 'true');
      media.appendChild(b);
    }
    return b;
  }
  function clear(media) {
    var b = media.querySelector(':scope > .fh-hero-band');
    if (b) b.remove();
  }

  var canvas = document.createElement('canvas');
  var ctx = canvas.getContext('2d', { willReadFrequently: true });

  function process(slide) {
    var media = slide.querySelector('.banner__media');
    var box = slide.querySelector('.banner__box');
    if (!media || !box) return;
    var img = media.querySelector('img');
    var mr = media.getBoundingClientRect();
    var br = box.getBoundingClientRect();
    if (!mr.width || !mr.height || !br.width) { clear(media); return; }

    /* Text in its own solid box, or not over the photo (e.g. below it on phones): nothing to do. */
    var bg = rgba(getComputedStyle(box).backgroundColor);
    if (bg && bg.a >= 0.9) { clear(media); return; }
    if (br.top >= mr.bottom || br.bottom <= mr.top) { clear(media); return; }

    var items = textItems(box).filter(function (el) {
      var r = el.getBoundingClientRect();
      return r.bottom > mr.top && r.top < mr.bottom;
    });
    if (!items.length) { clear(media); return; }

    /* The section's own overlay (its "Image overlay opacity" setting) counts too. */
    var after = getComputedStyle(media, '::after');
    var ovc = rgba(after.backgroundColor);
    var existing = ovc ? Math.min(1, (parseFloat(after.opacity) || 0) * ovc.a) : 0;
    if (after.content === 'none') existing = 0;

    var alpha = SAFE;
    if (img && img.complete && img.naturalWidth) {
      try {
        var scale = Math.min(1, 600 / mr.width);
        var W = Math.max(1, Math.round(mr.width * scale)), H = Math.max(1, Math.round(mr.height * scale));
        canvas.width = W; canvas.height = H;
        var ir = img.getBoundingClientRect();
        var fit = getComputedStyle(img).objectFit;
        var s = fit === 'contain' ? Math.min(ir.width / img.naturalWidth, ir.height / img.naturalHeight)
                                  : Math.max(ir.width / img.naturalWidth, ir.height / img.naturalHeight);
        var dw = img.naturalWidth * s, dh = img.naturalHeight * s;
        var dx = (ir.left - mr.left) + (ir.width - dw) / 2, dy = (ir.top - mr.top) + (ir.height - dh) / 2;
        ctx.clearRect(0, 0, W, H);
        ctx.drawImage(img, dx * scale, dy * scale, dw * scale, dh * scale);
        var need = 0;
        items.forEach(function (el) {
          var tc = rgba(getComputedStyle(el).color) || { r: 255, g: 255, b: 255 };
          var lt = lum(tc.r, tc.g, tc.b);
          if (lt < 0.4) return; /* dark text: a dark band would not help it */
          var maxBg = (lt + 0.05) / target(el) - 0.05;
          var r = el.getBoundingClientRect();
          var x0 = Math.max(0, Math.floor((r.left - mr.left - 4) * scale)), y0 = Math.max(0, Math.floor((r.top - mr.top - 4) * scale));
          var x1 = Math.min(W, Math.ceil((r.right - mr.left + 4) * scale)), y1 = Math.min(H, Math.ceil((r.bottom - mr.top + 4) * scale));
          if (x1 <= x0 || y1 <= y0) return;
          var d = ctx.getImageData(x0, y0, x1 - x0, y1 - y0).data;
          /* Brightest pixels decide. Solve for the darkening they need. */
          var bright = [];
          for (var i = 0; i < d.length; i += 4) {
            if (d[i + 3] === 0) continue;
            bright.push([d[i], d[i + 1], d[i + 2], lum(d[i], d[i + 1], d[i + 2])]);
          }
          bright.sort(function (a, b) { return b[3] - a[3]; });
          bright = bright.slice(0, 40);
          var lo = 0, hi = 0.85;
          function ok(a) {
            var k = (1 - existing) * (1 - a);
            for (var j = 0; j < bright.length; j++) {
              if (lum(bright[j][0] * k, bright[j][1] * k, bright[j][2] * k) > maxBg) return false;
            }
            return true;
          }
          if (ok(0)) return;
          for (var n = 0; n < 18; n++) { var mid = (lo + hi) / 2; if (ok(mid)) hi = mid; else lo = mid; }
          need = Math.max(need, hi);
        });
        alpha = need;
      } catch (e) {
        alpha = SAFE; /* photo couldn't be read (e.g. hosted elsewhere): use the strength that always passes */
      }
    }

    if (alpha < 0.02) { clear(media); media.removeAttribute('data-fh-hero'); return; }
    alpha = Math.min(0.85, Math.round(alpha * 100 + 2) / 100);
    var last = Math.max.apply(null, items.map(function (el) { return el.getBoundingClientRect().bottom; }));
    var end = Math.min(mr.height, Math.round(last - mr.top + PAD));
    var b = band(media);
    b.style.background = 'linear-gradient(to bottom, rgba(0,0,0,' + alpha + ') 0px, rgba(0,0,0,' + alpha + ') ' + end +
      'px, rgba(0,0,0,0) ' + (end + FADE) + 'px)';
    media.setAttribute('data-fh-hero', alpha.toFixed(2));
  }

  function runAll() {
    document.querySelectorAll('.banner, .slideshow').forEach(function (banner) {
      var slides = banner.querySelectorAll('.slideshow__slide');
      if (slides.length) slides.forEach(function (s) { try { process(s); } catch (e) {} });
      else { try { process(banner); } catch (e) {} }
    });
  }

  var timer = 0;
  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(runAll, 120);
  }
  function start() {
    document.documentElement.classList.add('fh-hero-js');
    runAll();
    document.querySelectorAll('.banner__media img').forEach(function (img) {
      if (!img.complete) img.addEventListener('load', schedule, { once: true });
    });
    window.addEventListener('load', schedule);
    window.addEventListener('resize', schedule);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(schedule);
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(schedule);
      document.querySelectorAll('.banner__box').forEach(function (b) { ro.observe(b); });
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();

/**
 * "In the News" logo row (2026-09-28): the row's scrollbar is hidden (fh-hero-contrast.css), so mark which edge has
 * more logos past it (fh-more = more to the right, fh-less = more to the left) for the CSS fade.
 * Rollback: delete this block and the matching block in fh-hero-contrast.css.
 */
(function () {
  'use strict';
  if (window.__fhNewsRow) return;
  window.__fhNewsRow = true;
  function update(n) {
    n.classList.toggle('fh-more', n.scrollLeft + n.clientWidth < n.scrollWidth - 2);
    n.classList.toggle('fh-less', n.scrollLeft > 2);
  }
  function start() {
    document.querySelectorAll('.in_the_news_section .logo_Nav').forEach(function (n) {
      var go = function () { update(n); };
      n.addEventListener('scroll', go, { passive: true });
      window.addEventListener('resize', go);
      window.addEventListener('load', go);
      n.querySelectorAll('img').forEach(function (img) { if (!img.complete) img.addEventListener('load', go, { once: true }); });
      go();
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
